import { FieldContext } from "rc-field-form";
import type { FormInstance } from "rc-field-form";
import type { ListField, ListOperations } from "rc-field-form/es/List";
import * as React from "react";
import { KuikaFormList } from "../../../ui-kit/kuika-form-page/kuika-form-page";
import { DraftRowBlankTest, IDraftRowPlan, planTrailingDraftRow } from "./draft-rows";

/** One row of the list, as the grid draws it. */
export interface IEditGridListRow {
  /** The row's name inside the list (its index) — `KuikaFormField name={[row.name, "key"]}`. */
  name: number;
  /** Stable for as long as the row lives, whatever is typed into it: the React key and nav id. */
  key: number;
  index: number;
  /**
   * The trailing blank row — where the next row is written. It has no delete, and its controls
   * carry the grid's `…Draft…` test ids. Always the last row: the list keeps exactly one blank
   * row at its end, so the last row IS the draft.
   */
  isDraft: boolean;
}

export interface IEditGridListApi {
  /**
   * Call from a cell's `onChange` (it runs after the form took the value). Brings the list back to
   * one blank row at its end: typing into the draft appends a fresh one under it, and clearing
   * the last real row back to nothing folds the draft below it away.
   */
  settle: () => void;
  remove: (index: number) => void;
}

export interface IKuikaEditGridFormListProps<T> {
  /** The list's path in the form store — also what spells its fields' DOM ids. */
  name: (string | number)[];
  isBlank: DraftRowBlankTest<T>;
  makeBlank: () => T;
  children: (rows: IEditGridListRow[], api: IEditGridListApi) => React.ReactNode;
}

interface IDraftRowKeeperProps {
  settle: () => void;
}

/**
 * Keeps the invariant when the rows are replaced from outside — a datasource loaded, a mode
 * switched back, an auth type that brings its own rows — none of which knows about the draft.
 * A layout effect, so the added row is on screen in the same frame as the rows it follows.
 * `rc-field-form`'s List re-renders only when the list itself changes (a load, `add`/`remove`),
 * never while a cell is typed in, so this runs exactly when the rows may have lost their draft.
 *
 * Except on mount. This sits inside the List, and layout effects run children-first: the List
 * registers with the form in its own `componentDidMount`, AFTER this, so an `add` from the first
 * layout pass lands in the store with nobody told to redraw. The mount's settle waits for the
 * passive pass, when the List is listening. A page that loads its rows as it mounts never sees
 * the gap: that load re-renders the List, and the layout pass it triggers settles it.
 */
const DraftRowKeeper = ({ settle }: IDraftRowKeeperProps): null => {
  const isFirstLayout: React.MutableRefObject<boolean> = React.useRef<boolean>(true);

  React.useLayoutEffect((): void => {
    if (isFirstLayout.current) {
      isFirstLayout.current = false;
      return;
    }
    settle();
  });

  React.useEffect((): void => {
    settle();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- mount only; the layout pass covers every later render
  }, []);

  return null;
};

/**
 * `KuikaFormList` with the trailing draft row: the list always ends on exactly one blank row, the
 * row a user types the next entry into. Validation and saving are the host's, and both must read
 * the rows through `withoutBlankRows` — a blank row is where a row goes, not a row.
 */
export const KuikaEditGridFormList = <T,>({
  name,
  isBlank,
  makeBlank,
  children
}: IKuikaEditGridFormListProps<T>): React.ReactElement => {
  const form: FormInstance = React.useContext(FieldContext);

  return (
    <KuikaFormList name={name}>
      {(fields: ListField[], { add, remove }: ListOperations): React.ReactNode => {
        const settle = (): void => {
          const rows: T[] | undefined = form.getFieldValue(name);
          const plan: IDraftRowPlan = planTrailingDraftRow(rows, isBlank);
          if (plan.removeIndexes.length > 0) {
            remove(plan.removeIndexes);
          }
          if (plan.append) {
            add(makeBlank());
          }
        };
        const rows: IEditGridListRow[] = fields.map((field: ListField, index: number): IEditGridListRow => ({
          name: field.name,
          key: field.key,
          index,
          isDraft: index === fields.length - 1
        }));

        return (
          <>
            <DraftRowKeeper settle={settle} />
            {children(rows, { settle, remove: (index: number): void => remove(index) })}
          </>
        );
      }}
    </KuikaFormList>
  );
};
